import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import RecruiterLayout from '@/components/RecruiterLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Plus, Edit, Settings, FileText, Bell, Search, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useUserRole } from '@/hooks/useUserRole';
import { format } from 'date-fns';
import { 
  NotificationEventType, 
  NotificationTemplate, 
  NotificationConfiguration,
  NotificationDeliveryLog 
} from '@/components/notifications/types';

const priorityColors = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-100 text-red-600'
};

const NotificationAdmin = () => {
  const [eventTypes, setEventTypes] = useState<NotificationEventType[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [configurations, setConfigurations] = useState<NotificationConfiguration[]>([]);
  const [deliveryLogs, setDeliveryLogs] = useState<NotificationDeliveryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventTypeDialogOpen, setEventTypeDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<NotificationEventType | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<NotificationConfiguration | null>(null);
  const [logSearch, setLogSearch] = useState('');
  const { toast } = useToast();
  const { role, loading: roleLoading } = useUserRole();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventTypesRes, templatesRes, configsRes, logsRes] = await Promise.all([
        supabase.from('notification_event_types').select('*').order('category'),
        supabase.from('notification_templates').select('*, event_type:notification_event_types(*)'),
        supabase.from('notification_configurations').select('*, event_type:notification_event_types(*)'),
        supabase.from('notification_delivery_log').select('*, notification:notifications(*)').order('created_at', { ascending: false }).limit(100)
      ]);

      if (eventTypesRes.data) setEventTypes(eventTypesRes.data);
      if (templatesRes.data) setTemplates(templatesRes.data as unknown as NotificationTemplate[]);
      if (configsRes.data) setConfigurations(configsRes.data as unknown as NotificationConfiguration[]);
      if (logsRes.data) setDeliveryLogs(logsRes.data as unknown as NotificationDeliveryLog[]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveEventType = async (data: Partial<NotificationEventType>) => {
    try {
      if (selectedEventType) {
        const { error } = await supabase
          .from('notification_event_types')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', selectedEventType.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('notification_event_types')
          .insert([data]);
        if (error) throw error;
      }
      toast({ title: 'Event type saved' });
      setEventTypeDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const saveTemplate = async (data: Partial<NotificationTemplate>) => {
    try {
      if (selectedTemplate) {
        const { error } = await supabase
          .from('notification_templates')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', selectedTemplate.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('notification_templates')
          .insert([data]);
        if (error) throw error;
      }
      toast({ title: 'Template saved' });
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const saveConfiguration = async (data: Partial<NotificationConfiguration>) => {
    try {
      if (selectedConfig) {
        const { error } = await supabase
          .from('notification_configurations')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', selectedConfig.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('notification_configurations')
          .insert([data]);
        if (error) throw error;
      }
      toast({ title: 'Configuration saved' });
      setConfigDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const retryDelivery = async (logId: string) => {
    try {
      const { error } = await supabase
        .from('notification_delivery_log')
        .update({ status: 'pending', retry_count: 0 })
        .eq('id', logId);
      if (error) throw error;
      toast({ title: 'Retry scheduled' });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (roleLoading || loading) {
    return (
      <RecruiterLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RecruiterLayout>
    );
  }

  if (role !== 'admin') {
    return (
      <RecruiterLayout>
        <Card className="p-12 text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Admin access required</p>
        </Card>
      </RecruiterLayout>
    );
  }

  const filteredLogs = deliveryLogs.filter(log =>
    log.notification?.title?.toLowerCase().includes(logSearch.toLowerCase()) ||
    log.status.toLowerCase().includes(logSearch.toLowerCase())
  );

  return (
    <RecruiterLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Notification Administration</h1>
          <p className="text-muted-foreground">Manage event types, templates, and delivery configuration</p>
        </div>

        <Tabs defaultValue="events" className="space-y-4">
          <TabsList>
            <TabsTrigger value="events">Event Types</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="configs">Configurations</TabsTrigger>
            <TabsTrigger value="logs">Delivery Logs</TabsTrigger>
          </TabsList>

          {/* Event Types Tab */}
          <TabsContent value="events">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Event Types</h2>
                <Button onClick={() => { setSelectedEventType(null); setEventTypeDialogOpen(true); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Event Type
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventTypes.map(et => (
                    <TableRow key={et.id}>
                      <TableCell className="font-medium">{et.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{et.category}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{et.description}</TableCell>
                      <TableCell>
                        <Badge variant={et.is_active ? 'default' : 'secondary'}>
                          {et.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedEventType(et); setEventTypeDialogOpen(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Notification Templates</h2>
                <Button onClick={() => { setSelectedTemplate(null); setTemplateDialogOpen(true); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Template
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(tpl => (
                    <TableRow key={tpl.id}>
                      <TableCell className="font-medium">{tpl.name}</TableCell>
                      <TableCell>{tpl.event_type?.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{tpl.channel}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{tpl.subject}</TableCell>
                      <TableCell>
                        <Badge variant={tpl.is_active ? 'default' : 'secondary'}>
                          {tpl.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedTemplate(tpl); setTemplateDialogOpen(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Configurations Tab */}
          <TabsContent value="configs">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Notification Configurations</h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Channels</TableHead>
                    <TableHead>Throttling</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configurations.map(cfg => (
                    <TableRow key={cfg.id}>
                      <TableCell className="font-medium">{cfg.event_type?.name}</TableCell>
                      <TableCell>
                        <Badge className={priorityColors[cfg.priority]}>{cfg.priority}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {cfg.channels.map(ch => (
                            <Badge key={ch} variant="outline" className="text-xs">{ch}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {cfg.throttle_count && cfg.throttle_period_minutes
                          ? `${cfg.throttle_count} per ${cfg.throttle_period_minutes}min`
                          : 'None'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={cfg.is_enabled ? 'default' : 'secondary'}>
                          {cfg.is_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedConfig(cfg); setConfigDialogOpen(true); }}>
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Delivery Logs Tab */}
          <TabsContent value="logs">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Delivery Logs</h2>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search logs..."
                      value={logSearch}
                      onChange={(e) => setLogSearch(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Notification</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Retries</TableHead>
                    <TableHead>Last Attempt</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium max-w-xs truncate">
                        {log.notification?.title || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.channel}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          log.status === 'delivered' ? 'default' :
                          log.status === 'failed' ? 'destructive' :
                          'secondary'
                        }>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{log.retry_count}/{log.max_retries}</TableCell>
                      <TableCell>
                        {log.last_attempt_at ? format(new Date(log.last_attempt_at), 'PPp') : '-'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {log.error_message || '-'}
                      </TableCell>
                      <TableCell>
                        {log.status === 'failed' && log.retry_count < log.max_retries && (
                          <Button variant="ghost" size="sm" onClick={() => retryDelivery(log.id)}>
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Event Type Dialog */}
        <EventTypeDialog
          open={eventTypeDialogOpen}
          onOpenChange={setEventTypeDialogOpen}
          eventType={selectedEventType}
          onSave={saveEventType}
        />

        {/* Template Dialog */}
        <TemplateDialog
          open={templateDialogOpen}
          onOpenChange={setTemplateDialogOpen}
          template={selectedTemplate}
          eventTypes={eventTypes}
          onSave={saveTemplate}
        />

        {/* Configuration Dialog */}
        <ConfigurationDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          configuration={selectedConfig}
          onSave={saveConfiguration}
        />
      </div>
    </RecruiterLayout>
  );
};

// Sub-components for dialogs
const EventTypeDialog = ({ open, onOpenChange, eventType, onSave }: any) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (eventType) {
      setName(eventType.name);
      setDescription(eventType.description || '');
      setCategory(eventType.category);
      setIsActive(eventType.is_active);
    } else {
      setName('');
      setDescription('');
      setCategory('general');
      setIsActive(true);
    }
  }, [eventType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{eventType ? 'Edit Event Type' : 'Add Event Type'}</DialogTitle>
          <DialogDescription>Define a new notification event type</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., candidate_applied" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe when this event is triggered" />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="candidate">Candidate</SelectItem>
                <SelectItem value="job">Job</SelectItem>
                <SelectItem value="communication">Communication</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>Active</Label>
          </div>
          <Button className="w-full" onClick={() => onSave({ name, description, category, is_active: isActive })}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TemplateDialog = ({ open, onOpenChange, template, eventTypes, onSave }: any) => {
  const [name, setName] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  const [channel, setChannel] = useState<'in_app' | 'email' | 'sms'>('in_app');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setEventTypeId(template.event_type_id);
      setChannel(template.channel);
      setSubject(template.subject || '');
      setBody(template.body);
      setIsActive(template.is_active);
    } else {
      setName('');
      setEventTypeId('');
      setChannel('in_app');
      setSubject('');
      setBody('');
      setIsActive(true);
    }
  }, [template]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'Add Template'}</DialogTitle>
          <DialogDescription>Create or edit notification template</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Event Type</Label>
              <Select value={eventTypeId} onValueChange={setEventTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((et: any) => (
                    <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Channel</Label>
              <Select value={channel} onValueChange={(v: any) => setChannel(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_app">In-App</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {channel === 'email' && (
              <div>
                <Label>Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
            )}
          </div>
          <div>
            <Label>Body (use {'{{variable}}'} for placeholders)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>Active</Label>
          </div>
          <Button className="w-full" onClick={() => onSave({ name, event_type_id: eventTypeId, channel, subject, body, is_active: isActive })}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ConfigurationDialog = ({ open, onOpenChange, configuration, onSave }: any) => {
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [channels, setChannels] = useState<string[]>(['in_app']);
  const [isEnabled, setIsEnabled] = useState(true);
  const [throttleCount, setThrottleCount] = useState('');
  const [throttlePeriod, setThrottlePeriod] = useState('');

  useEffect(() => {
    if (configuration) {
      setPriority(configuration.priority);
      setChannels(configuration.channels);
      setIsEnabled(configuration.is_enabled);
      setThrottleCount(configuration.throttle_count?.toString() || '');
      setThrottlePeriod(configuration.throttle_period_minutes?.toString() || '');
    }
  }, [configuration]);

  const toggleChannel = (ch: string) => {
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Configuration</DialogTitle>
          <DialogDescription>Configure notification delivery settings for {configuration?.event_type?.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={(v: any) => setPriority(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Channels</Label>
            <div className="flex gap-2 mt-2">
              {['in_app', 'email', 'sms'].map(ch => (
                <Badge
                  key={ch}
                  variant={channels.includes(ch) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleChannel(ch)}
                >
                  {ch.replace('_', '-')}
                </Badge>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Throttle Count</Label>
              <Input
                type="number"
                value={throttleCount}
                onChange={(e) => setThrottleCount(e.target.value)}
                placeholder="Max notifications"
              />
            </div>
            <div>
              <Label>Throttle Period (minutes)</Label>
              <Input
                type="number"
                value={throttlePeriod}
                onChange={(e) => setThrottlePeriod(e.target.value)}
                placeholder="Time window"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
            <Label>Enabled</Label>
          </div>
          <Button className="w-full" onClick={() => onSave({
            priority,
            channels,
            is_enabled: isEnabled,
            throttle_count: throttleCount ? parseInt(throttleCount) : null,
            throttle_period_minutes: throttlePeriod ? parseInt(throttlePeriod) : null
          })}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationAdmin;
